<template>
  <div class="tools" :class="{ 'tools--mobile': isMobile }">
    <div v-if="generateImageData.activeTab === 'prompt'" class="tools__row d-flex">
      <ai-card :radius="16" class="tools__item d-flex align-center flex-1-0">
        <div class="tools__item__title">
          {{ $t('common.color') }}
        </div>
        <v-spacer />
        <colors-list v-model="generateImageData.color" />
      </ai-card>
      <!-- <ai-card v-if="generateImageData.activeTab === 'prompt'" :radius="16" class="tools__item d-flex align-center">
        <style-list class="styles-list" />
      </ai-card> -->
    </div>
    <ai-card v-if="!isMobile" :radius="16" class="tools__item tools__item--padding-small d-flex align-center">
      <sizes-list
        v-model:height="generateImageData.canvas.height"
        v-model:width="generateImageData.canvas.width"
        class="sizes"
      />
      <v-spacer />
      <div class="edit__workarea__actions">
        <ai-button theme="dark" @click="imageForActions = ''; generateImageData.products = []">{{ $t('services.reset') }}</ai-button>

        <ai-button :disabled="generationLoading" theme="gradient-primary" @click="generateBackgroundHandler">
          <template v-if="!generationLoading">
            {{ $t('editor.settings.generate') }}
          </template>
          <time-counter v-else />
        </ai-button>
      </div>
    </ai-card>

    <template v-else>
      <ai-card :radius="16" class="d-flex align-center tools__item">
        <sizes-list
          v-model:height="generateImageData.canvas.height"
          v-model:width="generateImageData.canvas.width"
          class="sizes"
        />
      </ai-card>

      <div class="edit__workarea__actions mt-4">
        <ai-button theme="dark" @click="imageForActions = ''" style="width: 50%;">{{ $t('services.reset') }}</ai-button>

        <ai-button :disabled="generationLoading" theme="gradient-primary" @click="generateBackgroundHandler" style="width: 50%;">
          <template v-if="!generationLoading">
            {{ $t('editor.settings.generate') }}
          </template>
          <time-counter v-else />
        </ai-button>
      </div>
    </template>
    <!-- <div
      v-if="isMobile"
      class="button-generate button-generate--mobile primary-gradient"
      :class="{ 'button-generate--disabled': generationLoading }"
    >
      <ai-clear-button :disabled="generationLoading" class="button-generate__button" @click="generateBackgroundHandler">
        {{ $t('editor.settings.generate-mobile') }}
      </ai-clear-button>
    </div> -->
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useEventBus } from '@vueuse/core';

import { useCommonStore } from '@/store/common';
import { useEditorStore } from '@/store/editor';
import { useServicesStore } from '@/store/services';

import { removeBase64Prefix } from '@/helpers/removeBase64Prefix';

import { IGenerateBackgroundOptions } from '@/store/editor/types';

import AiCard from '@/components/UI/AiCard/AiCard.vue';
import AiButton from '@/components/UI/AiButton/AiButton.vue';
// import AiClearButton from '@/components/UI/AiClearButton/AiClearButton.vue';

import ColorsList from '@/components/common/ColorsList/ColorsList.vue';
import SizesList from '@/components/common/SizesList/SizesList.vue';
// import StyleList from '@/components/pages/Generate/StyleList/StyleList.vue';
import TimeCounter from '@/components/common/TimeCounter/TimeCounter.vue';

const busError = useEventBus<string>('toast-error');

const { t } = useI18n();

const { generateImage } = useServicesStore();
const { generateImageData, aiCanvasRef } = storeToRefs(useEditorStore());
const { imageForActions, imageResult, imagePrev } = storeToRefs(useServicesStore());

const { isMobile } = storeToRefs(useCommonStore());

const buttonDisabled = ref<boolean>(false);
const generationLoading = ref<boolean>(false);

const generateBackgroundHandler = async (): Promise<void> => {
  if (aiCanvasRef.value) {
    aiCanvasRef.value.canvasUpdateData();
    setTimeout(async () => {
      generateImageData.value.loading = true;
      const options: IGenerateBackgroundOptions = {
        customize: {},
        transforms: generateImageData.value.products.map((product: any) => product.transform),
        size: {
          width: generateImageData.value.canvas.width,
          height: generateImageData.value.canvas.height,
        },
      };

      if (generateImageData.value.activeTab === 'theme') {
        if (generateImageData.value.theme !== null) {
          options.customize.themeId = generateImageData.value.theme.id;
        }
        // if (generateImageData.value.color) {
        //   options.customize.styleColor = generateImageData.value.color;
        // }
      } else if (generateImageData.value.activeTab === 'prompt') {
        options.customize.description = generateImageData.value.prompt || '';
        if (generateImageData.value.negativeList && generateImageData.value.negativeList.length) {
          options.customize.negative = generateImageData.value.negativeList.join(', ');
        }
        if (generateImageData.value.color) {
          options.customize.styleColor = generateImageData.value.color;
        }
        if (generateImageData.value.style) {
          options.customize.styleImageId = generateImageData.value.style.id;
        }
      }
      buttonDisabled.value = true;
      if (isMobile.value) {
        // const appElem = document.querySelector('body');
        const productGallery: HTMLElement | null = document.querySelector('.product-gallery');
        if (productGallery) {
          window.scrollTo({ top: productGallery.offsetTop, left: 0, behavior: 'smooth' });
        }
      }
      generationLoading.value = true;
      const images = generateImageData.value.products.map((product: any) => removeBase64Prefix(product.image));
      generateImage({
        action: 'create_background_for_image',
        images,
        params: options,
      })
        .then(async (res) => {
          imagePrev.value = imageForActions.value;
          const bg = await aiCanvasRef.value.getBackground();
          imageForActions.value = bg.enlargedBase64src;
          imageResult.value = `data:image/png;base64,${res.image}`;
        })
        .catch((e) => {
          busError.emit(e?.error || t('common.err-gen'));
        })
        .finally(() => {
          generationLoading.value = false;
          generateImageData.value.loading = false;
        });
    });
  }
};
</script>

<style scoped lang="scss" src="./ProductToolsBottom.scss" />
